// The universal input buffer (suggestion "universal input buffer, frozen in hit-stop"; Merge Conflict's card 6.1):
// every new press of a buffered action goes into one slot, the newest press winning and a priority order the game
// sets breaking ties within a step. The press stays for `lifetime` seconds of sim time (0.22 s by default) and each
// state consumes it at its first legal moment, so a press made a little early (during a swing's recovery, a landing,
// a hit-stop) still lands. The slot ages only on steps outside hit-stop: a press made while frozen survives the freeze.
//
// `Actions` owns one when given `buffer` options and steps it at the start of every sim step, after the step's press
// edges are latched; gameplay reads it with `actions.buffer.consume(...)`.

/** The buffer's default lifetime, seconds of (unfrozen) sim time. */
export const BUFFER_SECONDS = 0.22;

/** What the game passes in. */
export interface BufferOptions {
  /** The buffered actions, highest priority first (a tie inside one step goes to the earliest listed). */
  priority: readonly string[];
  /** Seconds a press stays buffered. Default `BUFFER_SECONDS`. */
  lifetime?: number;
  /** True while the sim is in hit-stop (e.g. `() => runtime.clock.hitStopRemaining > 0`): the slot doesn't age. */
  frozen?: () => boolean;
}

/** A buffer's state for a save. JSON-safe. */
export interface BufferState {
  action: string | null;
  age: number;
}

/** The planted defects for the buffer's checks. */
export interface BufferFaults {
  /** The slot ages during hit-stop too (the check "the buffer doesn't age in hit-stop" must go red). */
  agesInHitStop: boolean;
}

export class InputBuffer {
  /** Planted-defect switches (off in play). */
  readonly faults: BufferFaults = { agesInHitStop: false };
  readonly priority: readonly string[];
  readonly lifetime: number;
  private readonly frozen: () => boolean;
  private slot: string | null = null;
  private slotAge = 0;

  constructor(o: BufferOptions) {
    this.priority = [...o.priority];
    this.lifetime = o.lifetime ?? BUFFER_SECONDS;
    this.frozen = o.frozen ?? (() => false);
  }

  /** One sim step of `dt` seconds: age the slot (not in hit-stop) and drop it past its lifetime, then take this
   *  step's highest-priority new press, if any. `pressed` is true for exactly one step per press (Actions.pressed). */
  step(dt: number, pressed: (action: string) => boolean): void {
    if (this.slot !== null && (this.faults.agesInHitStop || !this.frozen())) {
      this.slotAge += dt;
      if (this.slotAge > this.lifetime + 1e-9) this.clear();
    }
    for (const a of this.priority)
      if (pressed(a)) {
        this.slot = a;
        this.slotAge = 0;
        break;
      }
  }

  /** The buffered action, or null. */
  get action(): string | null {
    return this.slot;
  }

  /** Seconds the buffered press has aged (frozen steps excluded). */
  get age(): number {
    return this.slotAge;
  }

  /** Whether `action` is buffered now. */
  has(action: string): boolean {
    return this.slot === action;
  }

  /** Take the buffered press if it is one of `accept` (any action when omitted); returns it, or null. A state calls
   *  this at its first legal moment and starts the move on a non-null result. */
  consume(accept?: string | readonly string[]): string | null {
    const a = this.slot;
    if (a === null) return null;
    if (accept !== undefined && (typeof accept === "string" ? accept !== a : !accept.includes(a))) return null;
    this.clear();
    return a;
  }

  /** Empty the slot. */
  clear(): void {
    this.slot = null;
    this.slotAge = 0;
  }

  snapshot(): BufferState {
    return { action: this.slot, age: this.slotAge };
  }

  restore(s: BufferState | undefined): void {
    this.slot = s?.action ?? null;
    this.slotAge = s?.age ?? 0;
  }
}
